import type React from "react";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { LoadingSpinner } from "components/LoadingSpinner/LoadingSpinner";
import { TEACHER_HOME_PAGE_ROUTES } from "constants/routes";
import { useGroups } from "hooks/useGroups";
import { observer } from "mobx-react";
import { Heading1, Heading4 } from "styles/elements/Headings";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useModal } from "hooks/useModal";
import { useRedirect } from "hooks/useRedirect";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { EncircledIcon } from "components/EncircledIcon";
import { TeacherModals } from "constants/modals-constants";

import { ParagraphLarge, ParagraphSmall } from "styles/elements/Texts";
import { CssButton, CssFlex, CssGrid, CssIcon } from "styles/helpers/layout";
import { StyledGroupCard, StyledNewGroupCard } from "./GroupListPage.styled";
import { CreateGroupModal } from "./components/CreateGroupModal/CreateGroupModal";

export const GroupListPage: React.FC = observer(() => {
  const { t } = useTranslation("groups");
  const { redirectToUrl } = useRedirect();
  const { teacherGroups: ownedGroups, getTeacherGroups, loading: isLoadingGroupList } = useGroups();

  const { openModal } = useModal();
  const { getStudentTermByDomain } = useDomainHandler();

  // Fetch list of groups created by the current user
  useEffect(() => {
    getTeacherGroups(true);
  }, []);

  const goToGroupDetails = (groupId: number) => {
    redirectToUrl(
      buildUrlWithPathParams(TEACHER_HOME_PAGE_ROUTES.EDIT_GROUP, {
        groupId,
      }),
    );
  };

  return (
    <>
      <Heading1 marginBottom={2}>{t("groups:groupList.title")}</Heading1>
      <ParagraphLarge>
        {t("groups:groupList.description", {
          studentTerm: getStudentTermByDomain({ usePlural: true }),
        })}
      </ParagraphLarge>
      {/* -- Group list container -- */}
      <CssGrid
        data-cy="group-card-container"
        gridTemplateColumns="repeat(4, 1fr)"
        gap={2}
        marginTop="3rem"
      >
        {/* -- New group placeholder -- */}
        <StyledNewGroupCard
          data-cy="new-group-card"
          onClick={() => openModal(TeacherModals.CreateGroup)}
        >
          <EncircledIcon iconName="fas fa-plus" variant="secondary" />
          <ParagraphLarge>{t("groups:groupList.newGroupCard.createGroup")}</ParagraphLarge>
        </StyledNewGroupCard>
        {/* -- Existing group list -- */}
        {isLoadingGroupList ? (
          <LoadingSpinner />
        ) : (
          ownedGroups.map((group) => (
            <StyledGroupCard
              key={group.id}
              data-cy="group-card"
              onClick={() => goToGroupDetails(group.id)}
            >
              <CssFlex flexDirection="column" justifyContent="space-between" gap={2}>
                <Heading4 data-cy="group-name">{group.name}</Heading4>
                <CssFlex gap={0.5} alignItems="center">
                  <CssIcon iconName="fad fa-users" iconSize="xs" />
                  <ParagraphSmall>
                    <Trans
                      components={{ countContainerTag: <span data-cy="group-student-count" /> }}
                      i18nKey="groups:groupList.groupCard.studentCount"
                      t={t}
                      values={{
                        studentCount: group.studentCount,
                        studentTerm: getStudentTermByDomain({
                          usePlural: group.studentCount !== 1,
                        }),
                      }}
                    />
                  </ParagraphSmall>
                </CssFlex>
              </CssFlex>
              <hr />
              <CssButton
                isBlock
                variant="primary-outline"
                onClick={() => goToGroupDetails(group.id)}
              >
                {t("groups:groupList.groupCard.buttonLabels.viewGroup")}
              </CssButton>
            </StyledGroupCard>
          ))
        )}
        {/* -- Create group modal -- */}
        <CreateGroupModal onGroupCreated={() => getTeacherGroups(true)} />
      </CssGrid>
    </>
  );
});
